import ph from 'posthog-js'
import * as React from 'react'
import * as va from '@vercel/analytics'

import { getId } from '@basehub/utils/id/index'
import {
  plainTextToYDocBase64State,
  commentExtensionsForTransformations,
  stateToJson,
  jsonToPlainText,
} from '@basehub/tiptap-api-utils'

import type { Editor } from '@tiptap/core'

import { List } from '../slash-command'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { useCommentKeyboardNavigationHelpers } from './input-handlers'
import {
  Block,
  isBlockWithChildren,
  isCommentBlock,
  isListBlock,
  isRichTextBlock,
  isReferenceBlock,
  RootBlock,
} from '@basehub/data/replicache/index'
import { useTabsStore } from '../../tabs/store'

import {
  type CommentBlock,
  type InstanceBlock,
} from '@basehub/data/replicache/index'
import { getCommentExtensions } from '~/lib/tiptap/extensions/comment'
import {
  BlockSwap,
  ChangeBlockTypeParams,
} from '@basehub/data/replicache/mutators/repo-space/block/mutators/change'

import { useSelectedTeamAndRepo } from '~/hooks-data/use-selected-team-and-repo'
import { useEditorMode } from '../../editor-mode-provider'
import { AIModelingInput } from '../empty-state/ai-modeling'
import { PrimitiveTipTapEditor } from '~/lib/tiptap/components/primitive-editor'
import { ENABLE_SCHEMA_ASSISTANT } from '~/app/api/ai/schema-assistant/_constants'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import {
  useBlockAwareness,
  useCollaborationData,
} from '~/hooks-data/use-repo-presence'
import { MutatorUserError } from '@basehub/data/replicache/error-handling'
import { toast } from 'sonner'
import { useEditorStore } from '../../editor-store'
import { useRepoStore } from '../../repo-store'
import {
  RepoUndoOps,
  useRepoUndoManager,
} from '~/replicache/frontend/undo-redo/manager'
import {
  CreateBlockParams,
  DeleteBlockParams,
} from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { getBlockFocusOp } from '~/replicache/frontend/undo-redo/util'
import { useTreeSlice } from '~/hooks/use-tree-slice'

export const CommentBlockInput = (props: {
  block: CommentBlock
  parentBlock: Block
  path: string
}) => {
  const editorMode = useEditorMode()

  if (editorMode !== 'write:all') {
    const plainText = jsonToPlainText(
      stateToJson(props.block.value.state) as any,
      commentExtensionsForTransformations
    )
      .replace(/\n/g, '<br/>')
      .trim()

    if (!plainText) return null

    return (
      <div className="min-h-[40.5px] py-2">
        <p
          className="text-dark-muted leading-[24.5px] text-sm"
          dangerouslySetInnerHTML={{
            __html: plainText,
          }}
        />
      </div>
    )
  }

  return <CommentWithEditAccess {...props} />
}

const CommentWithEditAccess = ({
  block,
  path,
  parentBlock,
}: {
  block: CommentBlock
  parentBlock: Block
  path: string
}) => {
  const { repoReplicache: replicache } = useSelectedTeamAndRepo()
  const lastKeyDownRef = React.useRef<
    | 'ArrowDown'
    | 'ArrowUp'
    | 'ArrowRight'
    | 'ArrowLeft'
    | 'Enter'
    | 'Backspace'
    | undefined
  >(undefined)
  const addTab = useTabsStore((s) => s.addTab)
  const undoManager = useRepoUndoManager()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)

  const editorMode = useEditorMode()
  const setSelectedBlockId = useEditorStore((s) => s.setSelectedBlockId)
  const setSelectedBlockAwareness = useEditorStore(
    (s) => s.setSelectedBlockAwareness
  )
  const setFocus = useRepoStore((s) => s.setFocus)
  const shouldAutoFocus = useRepoStore(
    (s) => s.focus?.blockId === idToSourceBlockId(block)
  )
  const isSelected = useEditorStore(
    (s) => s.selectedBlockId === idToSourceBlockId(block)
  )
  const [aiStatus, setAiStatus] = React.useState('')

  const activeTab = useTabsStore((s) => s.activeTab)

  const blockTreeManagerRef = useWorkingTreeStore((s) => s.treeRef)
  const rootBlockLastRevertedAt = useTreeSlice(
    blockTreeManagerRef.current,
    (t) => (t.root.value as RootBlock).lastRevertedAt
  )

  const { getAdjacentBlocks } = useCommentKeyboardNavigationHelpers({
    block,
    blockTreeManagerRef,
  })
  const hasChangedTypeRef = React.useRef(false)

  const [isPromptingAi, setIsPromptingAi] = React.useState(false)
  const handleSelectBlock = React.useCallback(
    async (
      swap: BlockSwap | 'ai',
      typeOptions?: InstanceBlock['typeOptions']
    ) => {
      if (swap === 'ai') {
        setIsPromptingAi(true)
        return
      }
      if (!replicache) throw new Error('no replicache')

      hasChangedTypeRef.current = true

      const params: ChangeBlockTypeParams = {
        blockId: block.id,
        idSeed: getId(),
        isoNow: new Date().toISOString(),
        ...swap,
        typeOptions: typeOptions ?? null,
        path,
      }

      const { newBlock, undo } = await replicache.mutate.changeBlockType(params)

      undoManager.current?.push({
        redo: [
          getBlockFocusOp({
            block: { id: newBlock.id, sourceBlockId: newBlock.sourceBlockId },
            preferredTabKey: activeTabKey,
            forcePreferredTabKey: true,
            focusType: 'block:value',
          }),
          { op: 'changeBlockType', args: params },
        ],
        undo: [
          getBlockFocusOp({
            block: { id: newBlock.id, sourceBlockId: newBlock.sourceBlockId },
            preferredTabKey: activeTabKey,
            forcePreferredTabKey: true,
            focusType: 'block:value',
          }),
          { op: 'blockBatchMutations', args: { mutations: undo } },
        ],
      })

      ph.capture('Block created', {
        source: 'Slash command',
        blockType: swap.newBlockType,
      })
      va.track('Block created', {
        source: 'Slash command',
        blockType: swap.newBlockType,
      })

      if (
        isBlockWithChildren(newBlock) &&
        !isListBlock(newBlock) &&
        !isRichTextBlock(newBlock) &&
        !isReferenceBlock(newBlock)
      ) {
        // open the new block if with children (except list blocks & rich text blocks)
        addTab({ key: newBlock.id, partiallyOpen: true })
        setFocus({ type: 'block:document-title', blockId: newBlock.id })
      } else {
        setFocus({ type: 'block:name', blockId: newBlock.id })
      }
    },
    [activeTabKey, addTab, block.id, path, replicache, setFocus, undoManager]
  )

  const isSlashCommandOpenRef = React.useRef(false)

  const { currentBlocks, posIdx } = React.useMemo(() => {
    if (!activeTab?.key) return {}
    const currDoc = blockTreeManagerRef.current?.getNode(activeTab.key)
    if (!currDoc || !isBlockWithChildren(currDoc.value)) return {}
    const currentBlocks =
      blockTreeManagerRef.current
        ?.getChildrenKeys(currDoc.key)
        .map((key) => blockTreeManagerRef.current?.getNode(key)?.value)
        .filter(Boolean) ?? []

    const posIdx = currentBlocks.findIndex((c) => c.id === block.id)

    return { currentBlocks, posIdx }
  }, [activeTab?.key, block.id, blockTreeManagerRef])

  React.useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (
        ![
          'ArrowDown',
          'ArrowRight',
          'ArrowUp',
          'ArrowLeft',
          'Enter',
          'Backspace',
        ].includes(e.key)
      ) {
        return
      }

      lastKeyDownRef.current = e.key as typeof lastKeyDownRef.current
    }

    document.addEventListener('keydown', handleGlobalKeyDown)

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown)
    }
  }, [])

  const user = useCollaborationData()
  const awarenessList = useBlockAwareness(block.id)
  const blockAwareness = React.useMemo(
    () => ({
      blockId: block.id,
      isSelected,
      setSelectedBlockAwareness: setSelectedBlockAwareness,
      remoteAwarenessList: awarenessList,
    }),
    [block.id, isSelected, setSelectedBlockAwareness, awarenessList]
  )

  return (
    <>
      {ENABLE_SCHEMA_ASSISTANT && (isPromptingAi || aiStatus === 'loading') ? (
        <>
          <AIModelingInput
            mode="continue"
            path={path}
            blockId={block.id}
            posIdx={posIdx ?? 0}
            currentBlocks={currentBlocks ?? []}
            autoFocus
            onStatusChange={setAiStatus}
          />
        </>
      ) : (
        <div className="min-h-[40.5px]">
          <PrimitiveTipTapEditor
            user={user}
            blockAwareness={blockAwareness}
            key={
              // discarded changes should re-initialize all local state so that yjs doesn't merge stuff
              block.createdAt +
              block.id +
              block.value.lastRevertedAt +
              block.isCommitted +
              rootBlockLastRevertedAt
            }
            instanceId={`comment-${block.id}-${block.value.lastRevertedAt}-${rootBlockLastRevertedAt}`}
            focusRestorationId={idToSourceBlockId(block)}
            className="text-sm cursor-text text-dark-muted outline-none"
            data-block-id={idToSourceBlockId(block)}
            data-comment-block
            clientId={replicache?.clientID}
            undo={{
              focusOp: {
                op: 'focus',
                args: {
                  preferredTabKey: activeTabKey,
                  focus: {
                    type: 'block:value',
                    blockId: idToSourceBlockId(block),
                  },
                },
              } satisfies RepoUndoOps[number],
              manager: undoManager.current,
              persist: 'sessionStorage',
            }}
            getExtensions={({ ...collab }) => {
              return getCommentExtensions({
                collaborationExtensionsOptions: collab,
                // we need to create a new comment below this one
                // taking into account where the cursor is positioned when pressing Enter
                onFocus: function (this: { editor: Editor }) {
                  if (
                    lastKeyDownRef.current === 'ArrowDown' ||
                    lastKeyDownRef.current === 'ArrowUp' ||
                    lastKeyDownRef.current === 'Backspace'
                  ) {
                    return false
                  }

                  if (
                    lastKeyDownRef.current === 'ArrowRight' ||
                    lastKeyDownRef.current === 'Enter'
                  ) {
                    this.editor.commands.setTextSelection(0)
                  } else this.editor.commands.selectTextblockEnd()

                  return true
                },
                onPressEnter: ({ editor }) => {
                  if (isSlashCommandOpenRef.current) return false // don't prevent if slash command is open
                  if (!replicache) throw new Error('no replicache')
                  const selection = editor.view.state.selection
                  const text = editor.getText()
                  const textForNewBlock = text.slice(selection.to - 1)

                  // delete text that will be in the new block
                  editor.commands.deleteRange({
                    from: selection.from,
                    to: text.length + 2,
                  })

                  plainTextToYDocBase64State(
                    textForNewBlock,
                    commentExtensionsForTransformations
                  ).then(({ base64: newBlockBase64State }) => {
                    const id = getId()
                    const params: CreateBlockParams = {
                      data: {
                        id,
                        title: '',
                        type: 'comment',
                        value: {
                          state: newBlockBase64State,
                        },
                      },
                      path,
                      isoNow: new Date().toISOString(),
                      position: {
                        type: 'after',
                        targetId: block.id,
                      },
                      parentInstanceChildTarget:
                        parentBlock.instanceChildTarget,
                      idSeed: getId(),
                    }
                    replicache.mutate
                      .createBlock(params)
                      .then(({ newBlock, undo }) => {
                        if (!newBlock) return false
                        setSelectedBlockId(idToSourceBlockId(newBlock))
                        if (undo) {
                          undoManager.current?.push({
                            redo: [
                              getBlockFocusOp({
                                block: newBlock,
                                preferredTabKey: activeTabKey,
                                focusType: 'block:value',
                              }),
                              { op: 'createBlock', args: params },
                            ],
                            undo: [
                              getBlockFocusOp({
                                block: newBlock,
                                preferredTabKey: activeTabKey,
                                focusType: 'block:value',
                              }),
                              {
                                op: 'blockBatchMutations',
                                args: { mutations: undo },
                              },
                            ],
                          })
                        }
                      })
                  })

                  return true
                },
                onPressArrowUp: ({ editor }) => {
                  if (isSlashCommandOpenRef.current) return false // don't prevent if slash command is open

                  const selection = editor.view.state.selection
                  const text = editor.getText()
                  const previousText = text.slice(0, selection.from - 1)

                  if (previousText.includes('\n')) return false // don't prevent if isnt in the first line
                  const adjacentBlocks = getAdjacentBlocks()

                  // Is first child, so we focus doc title
                  if (!adjacentBlocks.prevBlock) {
                    if (!adjacentBlocks.parentBlock) return true

                    const isInsideAPreview =
                      document.activeElement?.closest<HTMLDivElement>(
                        `div[data-block-id="${idToSourceBlockId(
                          adjacentBlocks.parentBlock
                        )}"]`
                      )
                    editor.commands.blur()
                    setFocus({
                      type: isInsideAPreview
                        ? 'block:wrapper'
                        : 'block:document-title',
                      blockId: idToSourceBlockId(adjacentBlocks.parentBlock),
                    })

                    return false
                  }

                  // Is not first child, and has a comment block behind, so we focus it
                  editor.commands.blur()
                  const currentSelectionStart = editor.view.state.selection.from
                  editor.commands.setTextSelection(currentSelectionStart)
                  setSelectedBlockId(
                    idToSourceBlockId(adjacentBlocks.prevBlock.value)
                  )

                  return false
                },
                onPressArrowLeft: ({ editor }) => {
                  if (isSlashCommandOpenRef.current) return false // don't prevent if slash command is open
                  const selection = editor.view.state.selection
                  if (selection.from !== 1) return false
                  const adjacentBlocks = getAdjacentBlocks()

                  // Is first child, so we focus doc title
                  if (!adjacentBlocks.prevBlock) {
                    if (!adjacentBlocks.parentBlock) return true

                    setFocus({
                      type: 'block:document-title',
                      blockId: idToSourceBlockId(adjacentBlocks.parentBlock),
                    })

                    return false
                  }

                  // Is not first child, and has a comment block behind, so we focus it
                  if (isCommentBlock(adjacentBlocks.prevBlock.value)) {
                    setSelectedBlockId(
                      idToSourceBlockId(adjacentBlocks.prevBlock.value)
                    )
                    const currentSelectionStart =
                      editor.view.state.selection.from
                    editor.commands.setTextSelection(currentSelectionStart)
                    return true
                  }

                  return false
                },
                onPressArrowDown: ({ editor }) => {
                  if (isSlashCommandOpenRef.current) return false // don't prevent if slash command is open

                  const selection = editor.view.state.selection
                  const text = editor.getText()
                  const followingText = text.slice(selection.to - 1)

                  if (followingText.includes('\n')) return false // don't prevent if isnt in the first line
                  const adjacentBlocks = getAdjacentBlocks()

                  // Is last child
                  if (!adjacentBlocks.nextBlock) {
                    if (!adjacentBlocks.parentBlock) return true

                    const isInsideAPreview =
                      document.activeElement?.closest<HTMLDivElement>(
                        `div[data-block-id="${idToSourceBlockId(
                          adjacentBlocks.parentBlock
                        )}"]`
                      )

                    if (!isInsideAPreview) return false

                    editor.commands.blur()
                    return true
                  }

                  // Is not first child, and has a comment block behind, so we focus it
                  editor.commands.blur()
                  const currentSelectionStart = editor.view.state.selection.from
                  editor.commands.setTextSelection(currentSelectionStart)
                  setSelectedBlockId(
                    idToSourceBlockId(adjacentBlocks.nextBlock.value)
                  )

                  return false
                },
                onPressEscape: () => {
                  if (isSlashCommandOpenRef.current) return false // don't prevent if slash command is open

                  const adjacentBlocks = getAdjacentBlocks()

                  if (!adjacentBlocks.parentBlock) return false

                  setFocus({
                    type: 'block:document-title',
                    blockId: idToSourceBlockId(adjacentBlocks.parentBlock),
                  })

                  return true
                },
                onPressArrowRight: ({ editor }) => {
                  if (isSlashCommandOpenRef.current) return false // don't prevent if slash command is open

                  const selection = editor.view.state.selection
                  const text = editor.getText()

                  if (text.length > 0 && selection.to !== text.length + 1) {
                    return false
                  }

                  const adjacentBlocks = getAdjacentBlocks()
                  if (!adjacentBlocks.nextBlock) return true

                  // Is not first child, and has a comment block next to, so we focus it
                  if (isCommentBlock(adjacentBlocks.nextBlock.value)) {
                    setSelectedBlockId(
                      idToSourceBlockId(adjacentBlocks.nextBlock.value)
                    )
                    const currentSelectionStart = editor.view.state.selection.to
                    editor.commands.setTextSelection(currentSelectionStart)
                    return true
                  }

                  return false
                },
                onDeleteAtFirstChar: async ({ editor }) => {
                  if (!replicache) throw new Error('no replicache')

                  const { parentBlock, prevBlock } = getAdjacentBlocks()
                  const text = editor.getText()

                  // prevent if the comment has text
                  if (text) return

                  if (prevBlock) {
                    setFocus({
                      type: 'block:wrapper',
                      blockId: idToSourceBlockId(prevBlock.value),
                    })
                  } else if (parentBlock) {
                    setFocus({
                      type: 'block:document-title',
                      blockId: idToSourceBlockId(parentBlock),
                    })
                  }

                  const params: DeleteBlockParams = {
                    id: block.id,
                    path,
                    idSeed: getId(),
                    isoNow: new Date().toISOString(),
                  }

                  const result = await replicache.mutate
                    .deleteBlock(params)
                    .catch((err) => {
                      console.log(err)
                      if (err instanceof MutatorUserError) {
                        toast.error(err.reason)
                      } else {
                        toast.error(
                          'An error occurred while deleting the comment'
                        )
                      }
                    })

                  if (result?.undo && parentBlock) {
                    undoManager.current?.push({
                      redo: [
                        getBlockFocusOp({
                          block,
                          preferredTabKey: activeTabKey,
                          focusType: 'block:value',
                        }),
                        { op: 'deleteBlock', args: params },
                      ],
                      undo: [
                        {
                          op: 'blockBatchMutations',
                          args: { mutations: result.undo },
                        },
                        getBlockFocusOp({
                          block,
                          preferredTabKey: activeTabKey,
                          focusType: 'block:value',
                        }),
                      ],
                    })
                  }
                },
                onSlashCommandSelectBlock: handleSelectBlock,
                component: List,
                onSlashCommandOpen: () => {
                  isSlashCommandOpenRef.current = true
                },
                onSlashCommandClose: () => {
                  isSlashCommandOpenRef.current = false
                },
              })
            }}
            readOnly={editorMode !== 'write:all'}
            value={block.value.state}
            onUpdate={async (update) => {
              if (hasChangedTypeRef.current) return // don't update if we've changed the type
              try {
                await replicache?.mutate.updateBlock({
                  id: block.id,
                  instanceChildTarget: block.instanceChildTarget,
                  data: {
                    value: {
                      state: update,
                    },
                  },
                  path,
                  strategy: 'merge-deep',
                  isoNow: new Date().toISOString(),
                })
              } catch (e) {
                // maybe this block was deleted, ignore.
              }
            }}
            autoFocus={shouldAutoFocus}
            onFocus={() => {
              setFocus(null)
              setSelectedBlockId(idToSourceBlockId(block))
            }}
            onAutoFocus={() => {
              setFocus(null)
            }}
          />
        </div>
      )}
    </>
  )
}
